import clsx from 'clsx';
import { FC, MouseEvent, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { ToDoIconSecondFifth } from 'src/components/icons';
import { MSAppsIcon, MSPastWorkIcon } from 'src/components/icons/ms';
import { getAssignmentsStatusLabelMap } from 'src/constants/assignments.constants';
import { useCommonXapiContextPayloadHook } from 'src/hooks/use-common-xapi-context-payload.hook';
import {
  AssignmentsViewOptions,
  AssignmentsWorkTypeMap,
} from 'src/interfaces/assignment.types';
import { XApiEventKeys } from 'src/interfaces/event.types';
import eventService from 'src/services/event.service';
import {
  setActiveAssignmentsView,
  setActiveMSAssignmentsWorkType,
  useActiveAssignmentsViewSelector,
  useActiveMSAssignmentsWorkType,
} from 'src/store/features';
import { useAppDispatch } from 'src/store/redux';

import './content-toggle-buttons.scss';

const contentToggleTabs: {
  label: AssignmentsViewOptions;
  workType: AssignmentsWorkTypeMap;
  icon: ReactElement;
  assignmentsView: AssignmentsViewOptions;
}[] = [
  {
    label: AssignmentsViewOptions.APPS,
    workType: AssignmentsWorkTypeMap.TODO, // APPS view uses TODO work type for queries
    icon: <MSAppsIcon color="currentColor" className="toggle-buttons__icon" />,
    assignmentsView: AssignmentsViewOptions.APPS,
  },
  {
    label: AssignmentsViewOptions.TODO,
    workType: AssignmentsWorkTypeMap.TODO,
    icon: (
      <ToDoIconSecondFifth
        color="currentColor"
        className="toggle-buttons__icon"
      />
    ),
    assignmentsView: AssignmentsViewOptions.TODO,
  },
  {
    label: AssignmentsViewOptions.PAST_WORK,
    workType: AssignmentsWorkTypeMap.PAST_WORK,
    icon: (
      <MSPastWorkIcon color="currentColor" className="toggle-buttons__icon" />
    ),
    assignmentsView: AssignmentsViewOptions.PAST_WORK,
  },
];

export const ContentToggleButtons: FC = () => {
  const { t } = useTranslation();
  const assignmentsStatusLabelMap = getAssignmentsStatusLabelMap(t);

  const dispatch = useAppDispatch();

  const activeMSAssignmentsWorkType = useActiveMSAssignmentsWorkType();
  const activeAssignmentsView = useActiveAssignmentsViewSelector();
  const commonXapiContextPayload = useCommonXapiContextPayloadHook();

  const isAppsActive = activeAssignmentsView === AssignmentsViewOptions.APPS;
  const isTodoActive = activeAssignmentsView === AssignmentsViewOptions.TODO;
  const isPastWorkActive =
    activeAssignmentsView === AssignmentsViewOptions.PAST_WORK;

  const assignmentsViewStatusMap = {
    [AssignmentsViewOptions.APPS]: isAppsActive,
    [AssignmentsViewOptions.TODO]: isTodoActive,
    [AssignmentsViewOptions.PAST_WORK]: isPastWorkActive,
  };

  const handleContentToggleBtnClick = (
    e: MouseEvent,
    workTypeOption: AssignmentsWorkTypeMap,
    assignmentsViewOption: AssignmentsViewOptions,
  ) => {
    if (e.type !== 'contextmenu') {
      dispatch(setActiveMSAssignmentsWorkType(workTypeOption));

      const isSectionAlreadyActive =
        assignmentsViewStatusMap[assignmentsViewOption];

      if (!isSectionAlreadyActive) {
        dispatch(setActiveAssignmentsView(assignmentsViewOption));
      }
    }

    // don't send xApi event on left click on currently selected status filter button
    if (
      activeMSAssignmentsWorkType === workTypeOption &&
      e.type !== 'contextmenu'
    )
      return;

    eventService.send(
      XApiEventKeys.selectedStatusFilter,
      {
        result: {
          statusFiltersSelected: workTypeOption,
        },
      },
      {
        result: {
          isRightClick: e.type === 'contextmenu',
          ...commonXapiContextPayload,
        },
      },
    );
  };

  return (
    <div className="toggle-buttons">
      {contentToggleTabs.map(({ label, workType, icon, assignmentsView }) => {
        return (
          <button
            onClick={(e) =>
              handleContentToggleBtnClick(e, workType, assignmentsView)
            }
            onContextMenu={(e) =>
              handleContentToggleBtnClick(e, workType, assignmentsView)
            }
            className={clsx('toggle-buttons__button', {
              selected: label === activeAssignmentsView,
            })}
            key={label}
            aria-label={assignmentsStatusLabelMap[label]}
          >
            {icon}
            {assignmentsStatusLabelMap[label]}
          </button>
        );
      })}
    </div>
  );
};
